import {useEffect, useState} from 'react'
import {useQueryResponseLoading} from '../../core/QueryResponseProvider'
import {Task} from '../../core/_models'
import {Loading} from '../../../../../_metronic/partials/components/loading/Loading'
import {KanbanLane} from './KanbanLane'
import {useGetBacklogTasks} from '../../hooks/useGetBacklogTasks'
import {useGetInProgressTasks} from '../../hooks/useGetInProgressTasks'
import {useGetDoneTasks} from '../../hooks/useGetDoneTasks'
import {useMoveTask} from '../../hooks/useMoveTask'
import { useParams } from 'react-router-dom'

interface Column {
  id: string
  title: string
  color: string
  tasks: Task[]
}

const mockData = {
  columns: [
    {
      id: 'backlog',
      title: 'Backlog',
      color: 'backlog',
      tasks: [],
    },
    {
      id: 'em andamento',
      title: 'Em Andamento',
      color: 'in-progress',
      tasks: [],
    },
    {
      id: 'entregue',
      title: 'Entregue',
      color: 'done',
      tasks: [],
    },
  ],
}

const TasksByUserKanban = () => {
  const [columns, setColumns] = useState<Column[]>(mockData.columns)
  const [draggedTask, setDraggedTask] = useState<Task | null>(null)
  const [draggedFromColumn, setDraggedFromColumn] = useState<string | null>(null)
  const [draggedColumn, setDraggedColumn] = useState<Column | null>(null)
  const [draggedColumnIndex, setDraggedColumnIndex] = useState<number | null>(null)
  const [draggedTaskIndex, setDraggedTaskIndex] = useState<number | null>(null)
  const [showNewColumnModal, setShowNewColumnModal] = useState(false)
  const [newColumnName, setNewColumnName] = useState('')
  const {mutateAsync: moveTaskFn} = useMoveTask()
  const {userId} = useParams()

  const {data: backlogTasks = []} = useGetBacklogTasks({userId})
  const {data: inProgressTasks = []} = useGetInProgressTasks({userId})
  const {data: doneTasks = []} = useGetDoneTasks({userId})

  const isLoading = useQueryResponseLoading()

  useEffect(() => {
    const newColumns = [
      {
        id: 'backlog',
        title: 'Backlog',
        color: 'backlog',
        tasks: backlogTasks,
      },
      {
        id: 'em andamento',
        title: 'Em Andamento',
        color: 'in-progress',
        tasks: inProgressTasks,
      },
      {
        id: 'entregue',
        title: 'Entregue',
        color: 'done',
        tasks: doneTasks,
      },
    ]

    setColumns([...newColumns])
  }, [backlogTasks, inProgressTasks, doneTasks])

  const handleDragStart = (task: Task, columnId: string, taskIndex: number) => {
    setDraggedTask(task)
    setDraggedFromColumn(columnId)
    setDraggedTaskIndex(taskIndex)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = async (targetColumnId: string, dropIndex?: number) => {
    if (!draggedTask || !draggedFromColumn) return

    if (draggedFromColumn === targetColumnId) return

    moveTaskFn({draggedTask, fromColumn: draggedFromColumn, toColumn: targetColumnId})

    setDraggedFromColumn(null)
    setDraggedTask(null)
  }

  const handleAddNewColumn = () => {
    if (newColumnName.trim()) {
      const newColumn: Column = {
        id: `column-${Date.now()}`,
        title: newColumnName.trim(),
        color: 'custom-gray', // Cor cinza suave para novas colunas
        tasks: [],
      }

      setColumns((prevColumns) => [...prevColumns, newColumn])
      setNewColumnName('')
      setShowNewColumnModal(false)
    }
  }

  const handleNewColumnModalClose = () => {
    setShowNewColumnModal(false)
    setNewColumnName('')
  }

  const handleColumnDragStart = (column: Column, index: number) => {
    setDraggedColumn(column)
    setDraggedColumnIndex(index)
  }

  const handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleColumnDrop = (dropIndex: number) => {
    if (draggedColumn === null || draggedColumnIndex === null || draggedColumnIndex === dropIndex) {
      return
    }

    setColumns((prevColumns) => {
      const newColumns = [...prevColumns]
      const [draggedItem] = newColumns.splice(draggedColumnIndex, 1)
      newColumns.splice(dropIndex, 0, draggedItem)
      return newColumns
    })

    setDraggedColumn(null)
    setDraggedColumnIndex(null)
  }

  const handleColumnDragEnd = () => {
    setDraggedColumn(null)
    setDraggedColumnIndex(null)
  }

  const handleTaskReorder = (columnId: string, dropIndex: number) => {
    if (!draggedTask || !draggedFromColumn || !draggedTaskIndex) {
      return
    }

    setColumns((prevColumns) => {
      const newColumns = [...prevColumns]
      const column = newColumns.find((col) => col.id === columnId)

      if (column && draggedFromColumn === columnId) {
        // Reordenar na mesma coluna
        const newTasks = [...column.tasks]
        const [draggedItem] = newTasks.splice(draggedTaskIndex, 1)
        newTasks.splice(dropIndex, 0, draggedItem)
        column.tasks = newTasks
      }

      return newColumns
    })

    setDraggedTask(null)
    setDraggedFromColumn(null)
    setDraggedTaskIndex(null)
  }

  const handleEditColumnName = (columnId: string, newName: string) => {
    if (newName.trim()) {
      setColumns((prevColumns) =>
        prevColumns.map((col) => (col.id === columnId ? {...col, title: newName.trim()} : col))
      )
    }
  }

  return (
    <div id='kt_tasks_project_kanban_pane' className='pt-4'>
      <div className='kanban-demo-board'>
        <div className='d-flex gap-4' style={{minWidth: 'max-content'}}>
          {columns.map((column) => (
            <div key={column.id} style={{width: '280px', flexShrink: 0}}>
              <KanbanLane
                key={column.id}
                id={column.id}
                title={column.title}
                color={column.color}
                tasks={column.tasks}
                columnIndex={columns.indexOf(column)}
                onDragStart={handleDragStart}
                onTaskReorder={handleTaskReorder}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onEditName={handleEditColumnName}
                onColumnDragStart={handleColumnDragStart}
                onColumnDragOver={handleColumnDragOver}
                onColumnDrop={handleColumnDrop}
                onColumnDragEnd={handleColumnDragEnd}
                isDragging={draggedColumn?.id === column.id}
              />
            </div>
          ))}

          {/* Botão Nova Coluna */}
          {/* <div style={{width: '280px', flexShrink: 0}}>
            <div className='h-100 d-flex align-items-center justify-content-center'>
              <button
                className='btn btn-light-primary btn-lg d-flex flex-column align-items-center justify-content-center p-4'
                style={{
                  minHeight: '200px',
                  border: '2px dashed #e1e3ea',
                  backgroundColor: '#009ef70a',
                }}
                title='Adicionar nova coluna'
                onClick={() => setShowNewColumnModal(true)}
              >
                <i className='bi bi-plus-circle fs-1 text-primary mb-2'></i>
                <span className='text-muted fs-6'>Nova Coluna</span>
              </button>
            </div>
          </div> */}
        </div>

        {/* Modal Nova Coluna */}
        {showNewColumnModal && (
          <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
            <div className='modal-dialog modal-dialog-centered'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Nova Coluna</h5>
                  <button
                    type='button'
                    className='btn-close'
                    onClick={handleNewColumnModalClose}
                  ></button>
                </div>
                <div className='modal-body'>
                  <div className='mb-3'>
                    <label htmlFor='newColumnName' className='form-label'>
                      Nome da Coluna
                    </label>
                    <input
                      type='text'
                      className='form-control'
                      id='newColumnName'
                      value={newColumnName}
                      onChange={(e) => setNewColumnName(e.target.value)}
                      placeholder='Ex: Em Revisão, Teste, etc.'
                      autoFocus
                    />
                  </div>
                </div>
                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={handleNewColumnModalClose}
                  >
                    Cancelar
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={handleAddNewColumn}
                    disabled={!newColumnName.trim()}
                  >
                    Criar Coluna
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isLoading && <Loading />}
    </div>
  )
}
export {TasksByUserKanban}
